import React, { useEffect, useMemo } from 'react';
import { useProperty } from '@spotify-internal/remote-config-integration-react';
import styled from 'styled-components';
import { spacer16, Type } from '@spotify-internal/encore-web';

import Metatags from 'components/Metatags/Metatags';
import PageContainer from 'components/PageContainer';
import Breadcrumbs from 'components/Breadcrumbs';
import BlockSerializer from 'lib/sanity/ui/BlockSerializer';
import WarningBanner from 'components/WarningBanner/WarningBanner';
import analytics from 'utils/client/analytics/analytics';
import { useUbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { BlockSerializerUbiContext } from 'lib/sanity/ui/BlockSerializerUbiProvider';
import { TreatedArticleData, TreatmentState } from 'utils/remote-config/content/types';
import FeedbackWidgetControl from 'experiments/feedback-position';
import { useVerticalConfig } from 'providers/vertical';
import { Vertical } from 'utils/vertical';
import { samCtaForArticlePageBool } from 'utils/remote-config/properties';

import RelatedArticles from './RelatedArticles';
import StyledCard from './StyledCard';
import AccountPageCta from './AccountPageCta';

export interface ArticleProps {
  article: TreatedArticleData;
  articleSlug: string;
  isPlain: boolean;
}

const Title = styled(Type)`
  word-break: break-word;
  hyphens: auto;
`;

function getTreatmentIdentifier(article: TreatedArticleData, articleSlug: string): string {
  switch (article.treatmentState) {
    case TreatmentState.Control:
      return `${articleSlug}_control`;
    case TreatmentState.Treatment:
      return `${articleSlug}_treatment_${article.treatmentKey}`;
    default:
      return articleSlug;
  }
}

const Article = ({ article, isPlain, articleSlug }: ArticleProps) => {
  const articleIdentifier = getTreatmentIdentifier(article, articleSlug);
  const ubi = useUbiArticlePage(articleIdentifier);
  const { verticalName } = useVerticalConfig();
  const abTestShowSamCtaForArticlePage = useProperty(samCtaForArticlePageBool, { loadingValue: false });

  useEffect(() => {
    if (articleSlug !== articleIdentifier) {
      analytics.trackExperiment(articleIdentifier);
    }
    ubi.logArticleImpression();
  }, [articleIdentifier, ubi, articleSlug]);

  const { content, title, relatedArticles, category, banner, contactUsBanner } = article;

  const parentCategory = category?.parentCategory;

  const showAccountPageCta = useMemo(() => {
    return (
      verticalName === Vertical.CONSUMER &&
      abTestShowSamCtaForArticlePage &&
      // Only for `Account Help` and `Plan Help` categories
      category?.parentCategory?.showAccountPageCta
    );
  }, [category, verticalName, abTestShowSamCtaForArticlePage]);

  return (
    <>
      <Metatags title={title as string} urlPath={`article/${articleSlug}/`} />

      {!isPlain && <WarningBanner banner={banner} />}

      <PageContainer>
        {!isPlain && parentCategory && <Breadcrumbs parentCategory={parentCategory} article={article} ubi={ubi} />}

        {showAccountPageCta && <AccountPageCta ubi={ubi} />}

        {content && (
          <StyledCard vertical={verticalName}>
            <Title forwardedAs="h1" variant="altoBrio" paddingBottom={spacer16}>
              {title}
            </Title>
            <BlockSerializerUbiContext.Provider value={ubi}>
              <BlockSerializer value={content} />
            </BlockSerializerUbiContext.Provider>
          </StyledCard>
        )}

        {!isPlain && relatedArticles.length > 0 && (
          <StyledCard vertical={verticalName}>
            <RelatedArticles relatedArticles={relatedArticles} ubi={ubi} />
          </StyledCard>
        )}

        {contactUsBanner?.content && (
          <StyledCard className="encore-bright-accent-set">
            <Type as="div" variant="ballad" semanticColor="textBase">
              <BlockSerializer value={contactUsBanner.content} />
            </Type>
          </StyledCard>
        )}

        {!isPlain && <FeedbackWidgetControl articleIdentifier={articleIdentifier} ubi={ubi} />}
      </PageContainer>
    </>
  );
};

export default Article;
